import React, { useEffect, useState } from 'react'
import { UseInfiniteQueryResult } from '@tanstack/react-query'
import {
    FreeTextTable,
    kpiMap,
    KpiKey,
    FreeTextTableKpiBasedFreeText,
    FreeTextTableInclusionContext,
    FreeTextTableBespokeContext,
} from '@diversioteam/diversio-ds'
import { ErrorBoundary } from '@sentry/react'

import { withErrorBoundary } from 'config/withErrorBoundary/withErrorBoundary'
import { useExportBespokeFreeTexts } from 'hooks/freeTexts/export/useExportBespokeFreeTexts'
import { useExportFreeTexts } from 'hooks/freeTexts/export/useExportFreeTexts'
import { useGetBespokeFreeTexts } from 'hooks/freeTexts/useGetBespokeFreeTexts'
import { useGetBespokeSingleQuestion } from 'hooks/freeTexts/useGetBespokeSingleQuestion'
import { useGetFreeTexts } from 'hooks/freeTexts/useGetFreeTexts'
import { useGetFreeTextsFilters } from 'hooks/freeTexts/useGetFreeTextsFilters'
import { useDebounce } from 'hooks/useDebounce'
import {
    GetBespokeSingleQuestionResponse,
    GetFreeTextsParams,
    GetFreeTextsResponse,
} from 'api/actions/analyze/freeTexts/freeTextsActions.types'
import { getNextPageParam } from 'utils/getNextPageParam'

import { FreeTextsProps } from './freeTexts.types'

import './index.scss'

const FreeTextsWithoutEB = ({ selectedSurvey }: FreeTextsProps) => {
    const [bespokeResponses, setBespokeResponses] = useState<Record<string, GetBespokeSingleQuestionResponse[]>>({})
    const [engagementResponses, setEngagementResponses] = useState<Record<string, GetBespokeSingleQuestionResponse[]>>(
        {},
    )

    const defaultPainPoint = 'All'
    const defaultPrimaryContextTab = 'All'

    const [selectedPrimaryContextTab, setSelectedPrimaryContextTab] = useState(defaultPrimaryContextTab)

    const [searchValue, setSearchValue] = useState({
        ['Inclusion']: '',
        ['Bespoke']: '',
        ['Engagement']: '',
    })
    const debouncedSearchValue = useDebounce<Record<string, string>>(searchValue, 500)

    const [selectedPainPoint, setSelectedPainPoint] = useState<Record<string, string>>({
        [defaultPrimaryContextTab]: defaultPainPoint,
    })

    const selectedSurveyParams = {
        ...(selectedSurvey?.year && { year: selectedSurvey.year }),
        ...(selectedSurvey?.quarter && { quarter: selectedSurvey.quarter }),
    }

    const queryFreeTextsFilters = useGetFreeTextsFilters(selectedSurveyParams)

    useEffect(() => {
        if (queryFreeTextsFilters.data) {
            const newState = (queryFreeTextsFilters.data.inclusionFilterData || []).reduce((obj, { kpiName }) => {
                const kpi = queryFreeTextsFilters.data.kpiMapping.find(({ name }) => name === kpiName)?.code || ''

                return { ...obj, [kpi]: defaultPainPoint }
            }, selectedPainPoint)

            setSelectedPainPoint(newState)
        }
    }, [queryFreeTextsFilters.data])

    const getPainPointParam = (kpi: KpiKey): string =>
        queryFreeTextsFilters.data?.painPointMapping?.find(({ title }) => title === selectedPainPoint[kpi])?.code || ''

    const getInclusionContextQueryParams = (kpi: KpiKey) => ({
        ...(debouncedSearchValue['Inclusion'] && { search: debouncedSearchValue['Inclusion'] }),

        ...(selectedPainPoint[kpi] !== 'All' && {
            pain_point: getPainPointParam(kpi),
        }),
        ...selectedSurveyParams,
    })

    const createHookConfig = (kpi: KpiKey): [GetFreeTextsParams, { enabled: boolean }] => [
        { kpi, ...getInclusionContextQueryParams(kpi) },
        {
            enabled:
                !!(queryFreeTextsFilters.data?.inclusionFilterData || []).find(
                    ({ kpiName }) => kpiName === kpiMap[kpi],
                ) && kpi === selectedPrimaryContextTab,
        },
    ]

    const queryAllFreeTexts = useGetFreeTexts(getInclusionContextQueryParams('All' as KpiKey), {
        enabled: !!queryFreeTextsFilters.data?.inclusionFilterData,
    })

    const kpiQueries: Partial<Record<KpiKey, UseInfiniteQueryResult<GetFreeTextsResponse, Error>>> = {
        ['INCLUSIVE_CULTURE']: useGetFreeTexts(...createHookConfig('INCLUSIVE_CULTURE')),
        ['CAREER_DEVELOPMENT']: useGetFreeTexts(...createHookConfig('CAREER_DEVELOPMENT')),
        ['FAIR_MANAGEMENT']: useGetFreeTexts(...createHookConfig('FAIR_MANAGEMENT')),
        ['WORKPLACE_FLEXIBILITY']: useGetFreeTexts(...createHookConfig('WORKPLACE_FLEXIBILITY')),
        ['WORKPLACE_SAFETY']: useGetFreeTexts(...createHookConfig('WORKPLACE_SAFETY')),
        ['RECRUITING_HIRING']: useGetFreeTexts(...createHookConfig('RECRUITING_HIRING')),
    }

    const mutationExportFreeTexts = useExportFreeTexts({
        ...(selectedPrimaryContextTab !== 'All' && {
            kpi: selectedPrimaryContextTab,
        }),
        ...getInclusionContextQueryParams(selectedPrimaryContextTab as KpiKey),
        export_format: 'csv',
    })

    const queryBespokeFreeTexts = useGetBespokeFreeTexts(
        {
            ...(debouncedSearchValue['Bespoke'] && { search: debouncedSearchValue['Bespoke'] }),
            category: 'bespoke',
            ...selectedSurveyParams,
        },
        {
            enabled: !!queryFreeTextsFilters.data?.bespokeFreeText?.bespoke,
        },
    )

    const mutationExportBespokeFreeTexts = useExportBespokeFreeTexts({
        ...(debouncedSearchValue['Bespoke'] && { search: debouncedSearchValue['Bespoke'] }),
        category: 'bespoke',
        ...selectedSurveyParams,
        export_format: 'csv',
    })

    const queryEngagementFreeTexts = useGetBespokeFreeTexts(
        {
            ...(debouncedSearchValue['Engagement'] && { search: debouncedSearchValue['Engagement'] }),
            category: 'engagement',
            ...selectedSurveyParams,
        },
        {
            enabled: !!queryFreeTextsFilters.data?.bespokeFreeText?.engagement,
        },
    )

    const mutationExportEngagementFreeTexts = useExportBespokeFreeTexts({
        ...(debouncedSearchValue['Engagement'] && { search: debouncedSearchValue['Engagement'] }),
        category: 'engagement',
        ...selectedSurveyParams,
        export_format: 'csv',
    })

    const mutationBespokeSingleQuestion = useGetBespokeSingleQuestion()

    const getBespokeResponses = async (category: 'bespoke' | 'engagement') => {
        const query = category === 'bespoke' ? queryBespokeFreeTexts : queryEngagementFreeTexts

        if (query.data) {
            for await (const question of query?.data) {
                await mutationBespokeSingleQuestion.mutateAsync(
                    {
                        bespoke_question: question.uuid,
                        category,
                        ...selectedSurveyParams,
                    },
                    {
                        onSuccess: async (data) => {
                            const setResponsesUpdateFn = (
                                prevState: Record<string, GetBespokeSingleQuestionResponse[]>,
                            ) => ({
                                ...prevState,
                                [question.uuid]: [data],
                            })

                            if (category === 'bespoke') {
                                return setBespokeResponses(setResponsesUpdateFn)
                            }

                            if (category === 'engagement') {
                                return setEngagementResponses(setResponsesUpdateFn)
                            }
                        },
                    },
                )
            }
        }
    }

    useEffect(() => {
        const fetchBespokeData = async () => {
            if (queryBespokeFreeTexts.data) {
                await getBespokeResponses('bespoke')
            }

            if (queryEngagementFreeTexts.data) {
                await getBespokeResponses('engagement')
            }
        }

        fetchBespokeData()
    }, [queryBespokeFreeTexts.data, queryEngagementFreeTexts.data])

    const handleSelectPainPoint = (kpiName: string, painPoint: string) => {
        const kpi =
            kpiName === 'All' ? kpiName : Object.keys(kpiMap).find((key) => kpiMap[key as KpiKey] === kpiName) || ''

        return setSelectedPainPoint((prevState) => ({
            ...prevState,
            [kpi]: painPoint,
        }))
    }

    const handleChangeSearchValue = (value: string, contextName: string) => {
        setSearchValue((prevState) => ({
            ...prevState,
            [contextName]: value,
        }))
    }

    const handleChangeTab = (kpiName: string) =>
        setSelectedPrimaryContextTab(Object.keys(kpiMap).find((key) => kpiMap[key as KpiKey] === kpiName) || kpiName)

    const getPainPoints = (kpi: KpiKey): string[] => [
        'All',
        ...((queryFreeTextsFilters.data?.inclusionFilterData || [])
            .find((data) => data.kpiName === kpiMap[kpi])
            ?.painPoints?.map(({ painPointTitle }) => painPointTitle) || []),
    ]

    const getInclusionItems = (
        query: UseInfiniteQueryResult<GetFreeTextsResponse, Error>,
        kpi: KpiKey,
    ): FreeTextTableKpiBasedFreeText[] => {
        if (query.data?.pages[0].results.length === 0) {
            return []
        }

        return [
            {
                text: query.data?.pages[0].question || '',
                responses:
                    query.data?.pages?.flatMap(({ results }) =>
                        results?.map(
                            ({ painPoint, text, textSearchResult, translatedText, translatedTextSearchResult }) => ({
                                painPoint: selectedPainPoint[kpi] === 'All' ? painPoint.title : '',
                                text: translatedTextSearchResult || textSearchResult || translatedText || text,
                            }),
                        ),
                    ) || [],
                hasMore: query.data?.pages[query.data?.pages.length - 1].next !== null,
                onLoadMore: query.fetchNextPage,
            },
        ]
    }

    const kpiItems = (Object.entries(kpiQueries) as [KpiKey, UseInfiniteQueryResult<GetFreeTextsResponse, Error>][])
        .filter(
            ([kpi, _query]) =>
                queryFreeTextsFilters.data?.inclusionFilterData?.findIndex(({ kpiName }) => kpiName === kpiMap[kpi]) !==
                -1,
        )
        .map(([kpi, query]) => ({
            name: kpiMap[kpi],
            painPoints: getPainPoints(kpi),
            selectedPainPoint: selectedPainPoint[kpi],
            items: getInclusionItems(query, kpi),
        }))

    if (!queryFreeTextsFilters.data) {
        return null
    }

    const hasInclusion = queryFreeTextsFilters.data.inclusionFilterData !== null
    const hasBespoke =
        queryFreeTextsFilters.data.bespokeFreeText?.bespoke || queryFreeTextsFilters.data.bespokeFreeText?.engagement

    const inclusionContext: FreeTextTableInclusionContext = {
        name: 'Inclusion',
        searchValue: searchValue['Inclusion'],
        onChangeTab: handleChangeTab,
        onSelectPainPoint: handleSelectPainPoint,
        kpi: [
            {
                name: 'All',
                painPoints: [
                    'All',
                    ...(queryFreeTextsFilters.data.inclusionFilterData || []).flatMap(({ painPoints }) =>
                        painPoints.map(({ painPointTitle }) => painPointTitle),
                    ),
                ],
                selectedPainPoint: selectedPainPoint['All'],
                items:
                    queryAllFreeTexts.data?.pages[0] && queryAllFreeTexts.data?.pages[0].results.length > 0
                        ? [
                              {
                                  text: queryAllFreeTexts.data?.pages[0].question,
                                  responses: queryAllFreeTexts.data?.pages.flatMap(({ results }) =>
                                      results?.map(
                                          ({
                                              painPoint,
                                              text,
                                              translatedText,
                                              textSearchResult,
                                              translatedTextSearchResult,
                                          }) => ({
                                              kpi: painPoint.kpi.code,
                                              painPoint: selectedPainPoint['All'] === 'All' ? painPoint.title : '',
                                              text:
                                                  translatedTextSearchResult ||
                                                  textSearchResult ||
                                                  translatedText ||
                                                  text,
                                          }),
                                      ),
                                  ),
                                  hasMore:
                                      queryAllFreeTexts.data?.pages[queryAllFreeTexts.data?.pages.length - 1].next !==
                                      null,
                                  onLoadMore: queryAllFreeTexts.fetchNextPage,
                              },
                          ]
                        : [],
            },
            ...kpiItems,
        ],
    }

    const bespokeContexts: FreeTextTableBespokeContext[] = [
        ...(queryBespokeFreeTexts.data
            ? [
                  {
                      searchValue: searchValue['Bespoke'],
                      name: 'Bespoke',
                      items: queryBespokeFreeTexts.data.map(({ results, question, uuid }) => {
                          const nextPage = bespokeResponses?.[uuid]?.[bespokeResponses[uuid]?.length - 1]?.next

                          const getResponses = () => {
                              if (searchValue['Bespoke'] || Object.values(bespokeResponses).length === 0) {
                                  return results?.map(
                                      ({ text, translatedText, textSearchResult, translatedTextSearchResult }) =>
                                          translatedTextSearchResult || textSearchResult || translatedText || text,
                                  )
                              }

                              return (bespokeResponses[uuid] || []).flatMap(({ results }) =>
                                  results?.map(({ text }) => text),
                              )
                          }

                          return {
                              text: question,
                              responses: getResponses(),
                              hasMore: nextPage !== null && nextPage !== undefined && !searchValue['Bespoke'],
                              onLoadMore: () => {
                                  mutationBespokeSingleQuestion.mutateAsync(
                                      {
                                          bespoke_question: uuid,
                                          page: getNextPageParam(nextPage),
                                      },
                                      {
                                          onSuccess: async (data) => {
                                              setBespokeResponses((prevState) => ({
                                                  ...prevState,
                                                  [uuid]: [...prevState[uuid], data],
                                              }))
                                          },
                                      },
                                  )
                              },
                          }
                      }),
                  },
              ]
            : []),
        ...(queryEngagementFreeTexts.data
            ? [
                  {
                      searchValue: searchValue['Engagement'],
                      name: 'Engagement',
                      items: queryEngagementFreeTexts.data.map(({ results, question, uuid }) => {
                          const nextPage = engagementResponses?.[uuid]?.[engagementResponses[uuid]?.length - 1]?.next

                          const getResponses = () => {
                              if (searchValue['Engagement'] || Object.values(engagementResponses).length === 0) {
                                  return results?.map(
                                      ({ text, translatedText, textSearchResult, translatedTextSearchResult }) =>
                                          translatedTextSearchResult || textSearchResult || translatedText || text,
                                  )
                              }

                              return (engagementResponses[uuid] || []).flatMap(({ results }) =>
                                  results?.map(({ text }) => text),
                              )
                          }

                          return {
                              text: question,
                              responses: getResponses(),
                              hasMore: nextPage !== null && nextPage !== undefined && !searchValue['Bespoke'],
                              onLoadMore: () => {
                                  mutationBespokeSingleQuestion.mutateAsync(
                                      {
                                          bespoke_question: uuid,
                                          page: getNextPageParam(nextPage),
                                      },
                                      {
                                          onSuccess: async (data) => {
                                              setEngagementResponses((prevState) => ({
                                                  ...prevState,
                                                  [uuid]: [...prevState[uuid], data],
                                              }))
                                          },
                                      },
                                  )
                              },
                          }
                      }),
                  },
              ]
            : []),
    ]

    const handleDownload = (contextName: string) => {
        if (contextName === 'Inclusion') {
            return mutationExportFreeTexts.mutate()
        }

        if (contextName === 'Bespoke') {
            return mutationExportBespokeFreeTexts.mutate()
        }

        if (contextName === 'Engagement') {
            return mutationExportEngagementFreeTexts.mutate()
        }
    }

    if (queryAllFreeTexts.data || queryBespokeFreeTexts.data || queryBespokeFreeTexts.data) {
        if (hasInclusion && hasBespoke) {
            return (
                <ErrorBoundary>
                    <FreeTextTable
                        key="both-contexts"
                        inclusionContext={inclusionContext}
                        bespokeContexts={bespokeContexts}
                        onChangeSearchValue={handleChangeSearchValue}
                        onDownload={handleDownload}
                    />
                </ErrorBoundary>
            )
        }

        if (hasInclusion) {
            return (
                <ErrorBoundary>
                    <FreeTextTable
                        key="inclusion-only"
                        inclusionContext={inclusionContext}
                        onChangeSearchValue={handleChangeSearchValue}
                        onDownload={handleDownload}
                    />
                </ErrorBoundary>
            )
        }

        if (hasBespoke) {
            return (
                <ErrorBoundary>
                    <FreeTextTable
                        key="bespoke-only"
                        bespokeContexts={bespokeContexts}
                        onChangeSearchValue={handleChangeSearchValue}
                        onDownload={handleDownload}
                    />
                </ErrorBoundary>
            )
        }
    }

    return null
}

export const FreeTexts = withErrorBoundary(FreeTextsWithoutEB, {})
